import React, { useState, useEffect, useCallback } from 'react';
import * as dpService from 'features/dp/services/dpService';

/* ── Document categories ───────────────────────────────────── */
const DOC_CATEGORIES = {
  foto:                    'Foto 3x4',
  rg:                      'RG',
  cnh:                     'CNH',
  ctps:                    'CTPS',
  titulo_eleitor:          'Título de Eleitor',
  atestado:                'Atestado Médico',
  comprovante_residencia:  'Comprovante de Residência',
  certidao:                'Certidão',
  certificado:             'Certificado/Diploma',
  contrato:                'Contrato',
  outro:                   'Outro',
};

/* ── Status badge map ──────────────────────────────────────── */
const STATUS_MAP = {
  ativo:       { label: 'Ativo',       cls: 'bg-emerald-100 text-emerald-800' },
  ferias:      { label: 'Férias',      cls: 'bg-blue-100 text-blue-800' },
  afastado:    { label: 'Afastado',    cls: 'bg-amber-100 text-amber-800' },
  demitido:    { label: 'Demitido',    cls: 'bg-red-100 text-red-800' },
  experiencia: { label: 'Experiência', cls: 'bg-purple-100 text-purple-800' },
};

/* ── Select option sets ────────────────────────────────────── */
const SEXO_OPTIONS = [
  { value: '',  label: 'Selecione' },
  { value: 'M', label: 'Masculino' },
  { value: 'F', label: 'Feminino' },
];

const ESTADO_CIVIL_OPTIONS = [
  { value: '',              label: 'Selecione' },
  { value: 'solteiro',     label: 'Solteiro(a)' },
  { value: 'casado',       label: 'Casado(a)' },
  { value: 'divorciado',   label: 'Divorciado(a)' },
  { value: 'viuvo',        label: 'Viúvo(a)' },
  { value: 'uniao_estavel', label: 'União Estável' },
];

const RACA_COR_OPTIONS = [
  { value: '',  label: 'Selecione' },
  { value: '1', label: 'Branca' },
  { value: '2', label: 'Preta' },
  { value: '3', label: 'Parda' },
  { value: '4', label: 'Amarela' },
  { value: '5', label: 'Indígena' },
  { value: '6', label: 'Não informado' },
];

const GRAU_INSTRUCAO_OPTIONS = [
  { value: '',   label: 'Selecione' },
  { value: '01', label: 'Fund. Incompleto' },
  { value: '02', label: 'Fund. Completo' },
  { value: '03', label: 'Médio Incompleto' },
  { value: '04', label: 'Médio Completo' },
  { value: '05', label: 'Superior Incompleto' },
  { value: '06', label: 'Superior Completo' },
  { value: '07', label: 'Pós-graduação' },
  { value: '08', label: 'Mestrado' },
  { value: '09', label: 'Doutorado' },
];

const TIPO_CONTRATO_OPTIONS = [
  { value: '',              label: 'Selecione' },
  { value: 'indeterminado', label: 'Indeterminado' },
  { value: 'determinado',   label: 'Determinado' },
  { value: 'experiencia',   label: 'Experiência' },
];

const TIPO_SALARIO_OPTIONS = [
  { value: '',          label: 'Selecione' },
  { value: 'mensal',    label: 'Mensal' },
  { value: 'hora',      label: 'Hora' },
  { value: 'diario',    label: 'Diário' },
  { value: 'semanal',   label: 'Semanal' },
  { value: 'quinzenal', label: 'Quinzenal' },
];

const TIPO_JORNADA_OPTIONS = [
  { value: '',              label: 'Selecione' },
  { value: 'normal',        label: 'Normal' },
  { value: '12x36',         label: '12x36' },
  { value: 'parcial',       label: 'Parcial' },
  { value: 'teletrabalho',  label: 'Teletrabalho' },
];

const UF_OPTIONS = [
  { value: '', label: 'UF' },
  ...('AC,AL,AM,AP,BA,CE,DF,ES,GO,MA,MG,MS,MT,PA,PB,PE,PI,PR,RJ,RN,RO,RR,RS,SC,SE,SP,TO'
    .split(',')
    .map((uf) => ({ value: uf, label: uf }))),
];

const TIPO_CONTA_OPTIONS = [
  { value: '',          label: 'Selecione' },
  { value: 'corrente',  label: 'Conta Corrente' },
  { value: 'poupanca',  label: 'Poupança' },
  { value: 'salario',   label: 'Conta Salário' },
];

/* ── Reusable field helper ─────────────────────────────────── */
function DetailField({ label, field, obj, setObj, type = 'text', readOnly = false, select, podeEditar }) {
  if (select) {
    return (
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-[color:var(--text-secondary)]">{label}</label>
        <select
          className="px-3 py-2 rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] text-sm"
          value={obj[field] || ''}
          disabled={readOnly || !podeEditar}
          onChange={(e) => setObj((prev) => ({ ...prev, [field]: e.target.value }))}
        >
          {select.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-[color:var(--text-secondary)]">{label}</label>
      <input
        type={type}
        className={`px-3 py-2 rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] text-sm${readOnly ? ' opacity-60' : ''}`}
        value={obj[field] || ''}
        readOnly={readOnly || !podeEditar}
        onChange={(e) => {
          if (readOnly) return;
          setObj((prev) => ({ ...prev, [field]: e.target.value }));
        }}
      />
    </div>
  );
}

/* ── Accordion section header ──────────────────────────────── */
function SectionHeader({ label, sectionKey, secoes, onToggle }) {
  return (
    <button
      onClick={() => onToggle(sectionKey)}
      className="w-full flex items-center justify-between px-4 py-3 bg-[color:var(--surface)] border border-[color:var(--border)] rounded-lg hover:bg-[color:var(--surface-hover)] transition-colors"
    >
      <span className="font-semibold text-[color:var(--text)]">{label}</span>
      <span className="text-lg text-[color:var(--text-secondary)]">
        {secoes[sectionKey] ? '\u25BC' : '\u25B6'}
      </span>
    </button>
  );
}

/* ── Main component ────────────────────────────────────────── */
export default function FuncionarioDetalhe({ funcionarioId, onVoltar, podeEditar }) {
  const [funcionario, setFuncionario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const [editPessoal, setEditPessoal] = useState({});
  const [editContratual, setEditContratual] = useState({});

  const [secoes, setSecoes] = useState({
    pessoal: true,
    docs_id: false,
    contratual: false,
    documentos: true,
  });

  /* ── Document upload state ───────────────────────────────── */
  const [docs, setDocs] = useState([]);
  const [docLoading, setDocLoading] = useState(false);
  const [docCatFiltro, setDocCatFiltro] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCat, setUploadCat] = useState('outro');
  const [uploadDesc, setUploadDesc] = useState('');
  const [uploading, setUploading] = useState(false);

  /* ── Toggle accordion section ────────────────────────────── */
  const toggleSecao = (key) => setSecoes((s) => ({ ...s, [key]: !s[key] }));

  /* ── Load employee data ──────────────────────────────────── */
  const carregarFuncionario = useCallback(() => {
    setLoading(true);
    dpService.buscarFuncionario(funcionarioId)
      .then((res) => {
        const data = res.data.data || res.data;
        setFuncionario(data);

        const p = data.pessoa || {};
        setEditPessoal({
          nome: p.nome || '',
          nome_social: p.nome_social || '',
          dt_nascimento: p.dt_nascimento ? p.dt_nascimento.substring(0, 10) : '',
          sexo: p.sexo || '',
          estado_civil: p.estado_civil || '',
          nacionalidade: p.nacionalidade || '',
          raca_cor: p.raca_cor || '',
          grau_instrucao: p.grau_instrucao || '',
          nome_mae: p.nome_mae || '',
          nome_pai: p.nome_pai || '',
          pis_pasep: p.pis_pasep || '',
          telefone: p.telefone || '',
          email: p.email || '',
          chave_pix: p.chave_pix || '',
          rg_numero: p.rg_numero || '',
          rg_orgao_emissor: p.rg_orgao_emissor || '',
          rg_uf: p.rg_uf || '',
          rg_dt_emissao: p.rg_dt_emissao ? p.rg_dt_emissao.substring(0, 10) : '',
          ctps_numero: p.ctps_numero || '',
          ctps_serie: p.ctps_serie || '',
          ctps_uf: p.ctps_uf || '',
          ctps_dt_emissao: p.ctps_dt_emissao ? p.ctps_dt_emissao.substring(0, 10) : '',
          cnh_numero: p.cnh_numero || '',
          cnh_categoria: p.cnh_categoria || '',
          cnh_validade: p.cnh_validade ? p.cnh_validade.substring(0, 10) : '',
          titulo_eleitor_numero: p.titulo_eleitor_numero || '',
          titulo_eleitor_zona: p.titulo_eleitor_zona || '',
          titulo_eleitor_secao: p.titulo_eleitor_secao || '',
          endereco_logradouro: p.endereco_logradouro || '',
          endereco_numero: p.endereco_numero || '',
          endereco_complemento: p.endereco_complemento || '',
          endereco_bairro: p.endereco_bairro || '',
          endereco_cep: p.endereco_cep || '',
          endereco_cidade: p.endereco_cidade || '',
          endereco_uf: p.endereco_uf || '',
          banco_codigo: p.banco_codigo || '',
          banco_agencia: p.banco_agencia || '',
          banco_conta: p.banco_conta || '',
          banco_tipo_conta: p.banco_tipo_conta || '',
        });

        setEditContratual({
          dt_admissao: data.dt_admissao ? data.dt_admissao.substring(0, 10) : '',
          cargo_id: data.cargo_id || '',
          departamento_id: data.departamento_id || '',
          tipo_contrato: data.tipo_contrato || '',
          tipo_jornada: data.tipo_jornada || '',
          jornada_horas_semanais: data.jornada_horas_semanais || '',
          salario: data.salario || '',
          tipo_salario: data.tipo_salario || '',
        });
      })
      .catch((err) => console.error('Erro buscar func:', err))
      .finally(() => setLoading(false));
  }, [funcionarioId]);

  /* ── Load documents ──────────────────────────────────────── */
  const carregarDocs = useCallback(() => {
    setDocLoading(true);
    const params = docCatFiltro ? { categoria: docCatFiltro } : {};
    dpService.listarDocumentos(funcionarioId, params)
      .then((res) => {
        const data = res.data.data || res.data;
        setDocs(Array.isArray(data) ? data : []);
      })
      .catch((err) => console.error('Erro docs:', err))
      .finally(() => setDocLoading(false));
  }, [docCatFiltro, funcionarioId]);

  useEffect(() => { carregarFuncionario(); }, [carregarFuncionario]);
  useEffect(() => { carregarDocs(); }, [carregarDocs]);

  /* ── Save changes ────────────────────────────────────────── */
  const salvar = () => {
    if (!podeEditar) return;
    setSaving(true);
    setMsg(null);
    dpService.atualizarFuncionario(funcionarioId, {
      pessoal: editPessoal,
      contratual: editContratual,
    })
      .then(() => {
        setMsg({ tipo: 'ok', texto: 'Salvo com sucesso!' });
        carregarFuncionario();
      })
      .catch((err) => {
        setMsg({
          tipo: 'erro',
          texto: 'Erro ao salvar: ' + (err.response?.data?.message || err.message),
        });
      })
      .finally(() => setSaving(false));
  };

  /* ── Upload document ─────────────────────────────────────── */
  const handleUpload = () => {
    if (!uploadFile) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', uploadFile);
    fd.append('categoria', uploadCat);
    if (uploadDesc) fd.append('descricao', uploadDesc);

    dpService.uploadDocumento(funcionarioId, fd)
      .then(() => {
        setUploadFile(null);
        setUploadDesc('');
        carregarDocs();
      })
      .catch((err) => window.alert('Erro upload: ' + (err.response?.data?.message || err.message)))
      .finally(() => setUploading(false));
  };

  /* ── Delete document ─────────────────────────────────────── */
  const excluirDoc = (docId) => {
    if (!window.confirm('Excluir este documento?')) return;
    dpService.excluirDocumento(funcionarioId, docId)
      .then(() => carregarDocs())
      .catch((err) => window.alert('Erro: ' + err.message));
  };

  /* ── Field helper shorthand ──────────────────────────────── */
  const inp = (label, field, obj, setObj, opts = {}) => (
    <DetailField
      label={label}
      field={field}
      obj={obj}
      setObj={setObj}
      type={opts.type}
      readOnly={opts.readOnly}
      select={opts.select}
      podeEditar={podeEditar}
    />
  );

  /* ── Loading state ───────────────────────────────────────── */
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
      </div>
    );
  }

  /* ── Not found ───────────────────────────────────────────── */
  if (!funcionario) {
    return (
      <div className="text-center py-20">
        <p className="text-[color:var(--text-secondary)]">Funcionário não encontrado</p>
        <button onClick={onVoltar} className="mt-4 text-teal-600 hover:underline">Voltar</button>
      </div>
    );
  }

  const pessoa = funcionario.pessoa || {};
  const statusInfo = STATUS_MAP[funcionario.status] || { label: funcionario.status, cls: 'bg-gray-100 text-gray-800' };

  return (
    <div className="space-y-4">
      {/* ── Header bar ─────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3 bg-[color:var(--surface)] border border-[color:var(--border)] rounded-xl p-4">
        <div className="flex items-center gap-4">
          <button
            onClick={onVoltar}
            className="text-[color:var(--text-secondary)] hover:text-[color:var(--text)] text-xl"
          >
            &larr;
          </button>
          <div>
            <h2 className="text-xl font-bold text-[color:var(--text)]">
              {pessoa.nome || 'Funcionário'}{' '}
              <span className="text-sm font-normal text-[color:var(--text-secondary)]">
                #{funcionario.matricula || ''}
              </span>
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.cls}`}>
                {statusInfo.label}
              </span>
              {funcionario.cargo && (
                <span className="text-xs text-[color:var(--text-secondary)]">
                  {funcionario.cargo.nome}
                </span>
              )}
              {funcionario.departamento && (
                <span className="text-xs text-[color:var(--text-secondary)]">
                  {'• ' + funcionario.departamento.nome}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {msg && (
            <span className={`text-sm ${msg.tipo === 'ok' ? 'text-emerald-600' : 'text-red-500'}`}>
              {msg.texto}
            </span>
          )}
          {podeEditar && (
            <button
              onClick={salvar}
              disabled={saving}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50"
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          )}
        </div>
      </div>

      {/* ── Section 1: Dados Pessoais ──────────────────────── */}
      <SectionHeader label="Dados Pessoais" sectionKey="pessoal" secoes={secoes} onToggle={toggleSecao} />
      {secoes.pessoal && (
        <div className="bg-[color:var(--surface)] border border-[color:var(--border)] rounded-xl p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {inp('Nome', 'nome', editPessoal, setEditPessoal)}
            {inp('CPF', 'cpf', { cpf: pessoa.cpf_formatado || pessoa.cpf || '' }, () => {}, { readOnly: true })}
            {inp('Nascimento', 'dt_nascimento', editPessoal, setEditPessoal, { type: 'date' })}
            {inp('Sexo', 'sexo', editPessoal, setEditPessoal, { select: SEXO_OPTIONS })}
            {inp('Estado Civil', 'estado_civil', editPessoal, setEditPessoal, { select: ESTADO_CIVIL_OPTIONS })}
            {inp('Nacionalidade', 'nacionalidade', editPessoal, setEditPessoal)}
            {inp('Raça/Cor', 'raca_cor', editPessoal, setEditPessoal, { select: RACA_COR_OPTIONS })}
            {inp('Grau de Instrução', 'grau_instrucao', editPessoal, setEditPessoal, { select: GRAU_INSTRUCAO_OPTIONS })}
            {inp('PIS/PASEP', 'pis_pasep', editPessoal, setEditPessoal)}
            {inp('Nome da Mãe', 'nome_mae', editPessoal, setEditPessoal)}
            {inp('Nome do Pai', 'nome_pai', editPessoal, setEditPessoal)}
            {inp('Nome Social', 'nome_social', editPessoal, setEditPessoal)}
            {inp('Telefone', 'telefone', editPessoal, setEditPessoal)}
            {inp('Email', 'email', editPessoal, setEditPessoal, { type: 'email' })}
            {inp('Chave PIX', 'chave_pix', editPessoal, setEditPessoal)}
          </div>
        </div>
      )}

      {/* ── Section 2: Documentos de Identificação ─────────── */}
      <SectionHeader label="Documentos de Identificação" sectionKey="docs_id" secoes={secoes} onToggle={toggleSecao} />
      {secoes.docs_id && (
        <div className="bg-[color:var(--surface)] border border-[color:var(--border)] rounded-xl p-4">
          <div className="space-y-4">
            {/* RG */}
            <div>
              <h4 className="text-sm font-semibold text-[color:var(--text)] mb-2">RG</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {inp('Número', 'rg_numero', editPessoal, setEditPessoal)}
                {inp('Órgão Emissor', 'rg_orgao_emissor', editPessoal, setEditPessoal)}
                {inp('UF', 'rg_uf', editPessoal, setEditPessoal, { select: UF_OPTIONS })}
                {inp('Data Emissão', 'rg_dt_emissao', editPessoal, setEditPessoal, { type: 'date' })}
              </div>
            </div>

            {/* CTPS */}
            <div>
              <h4 className="text-sm font-semibold text-[color:var(--text)] mb-2">CTPS</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {inp('Número', 'ctps_numero', editPessoal, setEditPessoal)}
                {inp('Série', 'ctps_serie', editPessoal, setEditPessoal)}
                {inp('UF', 'ctps_uf', editPessoal, setEditPessoal, { select: UF_OPTIONS })}
                {inp('Data Emissão', 'ctps_dt_emissao', editPessoal, setEditPessoal, { type: 'date' })}
              </div>
            </div>

            {/* CNH */}
            <div>
              <h4 className="text-sm font-semibold text-[color:var(--text)] mb-2">CNH</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {inp('Número', 'cnh_numero', editPessoal, setEditPessoal)}
                {inp('Categoria', 'cnh_categoria', editPessoal, setEditPessoal)}
                {inp('Validade', 'cnh_validade', editPessoal, setEditPessoal, { type: 'date' })}
              </div>
            </div>

            {/* Título de Eleitor */}
            <div>
              <h4 className="text-sm font-semibold text-[color:var(--text)] mb-2">Título de Eleitor</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {inp('Número', 'titulo_eleitor_numero', editPessoal, setEditPessoal)}
                {inp('Zona', 'titulo_eleitor_zona', editPessoal, setEditPessoal)}
                {inp('Seção', 'titulo_eleitor_secao', editPessoal, setEditPessoal)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Section 3: Dados Contratuais e Endereço ────────── */}
      <SectionHeader label="Dados Contratuais e Endereço" sectionKey="contratual" secoes={secoes} onToggle={toggleSecao} />
      {secoes.contratual && (
        <div className="bg-[color:var(--surface)] border border-[color:var(--border)] rounded-xl p-4">
          <div className="space-y-4">
            {/* Contrato */}
            <div>
              <h4 className="text-sm font-semibold text-[color:var(--text)] mb-2">Contrato</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {inp('Admissão', 'dt_admissao', editContratual, setEditContratual, { type: 'date' })}
                {inp('Tipo Contrato', 'tipo_contrato', editContratual, setEditContratual, { select: TIPO_CONTRATO_OPTIONS })}
                {inp('Tipo Jornada', 'tipo_jornada', editContratual, setEditContratual, { select: TIPO_JORNADA_OPTIONS })}
                {inp('Horas Semanais', 'jornada_horas_semanais', editContratual, setEditContratual, { type: 'number' })}
                {inp('Salário (R$)', 'salario', editContratual, setEditContratual, { type: 'number' })}
                {inp('Tipo Salário', 'tipo_salario', editContratual, setEditContratual, { select: TIPO_SALARIO_OPTIONS })}
              </div>
            </div>

            {/* Endereço */}
            <div>
              <h4 className="text-sm font-semibold text-[color:var(--text)] mb-2">Endereço</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {inp('Logradouro', 'endereco_logradouro', editPessoal, setEditPessoal)}
                {inp('Número', 'endereco_numero', editPessoal, setEditPessoal)}
                {inp('Complemento', 'endereco_complemento', editPessoal, setEditPessoal)}
                {inp('Bairro', 'endereco_bairro', editPessoal, setEditPessoal)}
                {inp('CEP', 'endereco_cep', editPessoal, setEditPessoal)}
                {inp('Cidade', 'endereco_cidade', editPessoal, setEditPessoal)}
                {inp('UF', 'endereco_uf', editPessoal, setEditPessoal, { select: UF_OPTIONS })}
              </div>
            </div>

            {/* Dados Bancários */}
            <div>
              <h4 className="text-sm font-semibold text-[color:var(--text)] mb-2">Dados Bancários</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {inp('Código Banco', 'banco_codigo', editPessoal, setEditPessoal)}
                {inp('Agência', 'banco_agencia', editPessoal, setEditPessoal)}
                {inp('Conta', 'banco_conta', editPessoal, setEditPessoal)}
                {inp('Tipo Conta', 'banco_tipo_conta', editPessoal, setEditPessoal, { select: TIPO_CONTA_OPTIONS })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Section 4: Documentos (upload & list) ──────────── */}
      <SectionHeader label="Documentos" sectionKey="documentos" secoes={secoes} onToggle={toggleSecao} />
      {secoes.documentos && (
        <div className="bg-[color:var(--surface)] border border-[color:var(--border)] rounded-xl p-4 space-y-4">
          {/* Category filter pills */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setDocCatFiltro('')}
              className={`px-3 py-1 rounded-full text-xs font-medium ${
                !docCatFiltro
                  ? 'bg-teal-600 text-white'
                  : 'bg-[color:var(--surface-hover)] text-[color:var(--text-secondary)]'
              }`}
            >
              Todos
            </button>
            {Object.entries(DOC_CATEGORIES).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setDocCatFiltro(key)}
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  docCatFiltro === key
                    ? 'bg-teal-600 text-white'
                    : 'bg-[color:var(--surface-hover)] text-[color:var(--text-secondary)]'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Upload form */}
          {podeEditar && (
            <div className="border-2 border-dashed border-[color:var(--border)] rounded-xl p-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[200px]">
                  <label className="text-xs font-medium text-[color:var(--text-secondary)] block mb-1">
                    Arquivo
                  </label>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                    onChange={(e) => setUploadFile(e.target.files[0] || null)}
                    className="text-sm text-[color:var(--text)]"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-[color:var(--text-secondary)] block mb-1">
                    Categoria
                  </label>
                  <select
                    value={uploadCat}
                    onChange={(e) => setUploadCat(e.target.value)}
                    className="px-3 py-2 rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] text-sm"
                  >
                    {Object.entries(DOC_CATEGORIES).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1 min-w-[150px]">
                  <label className="text-xs font-medium text-[color:var(--text-secondary)] block mb-1">
                    Descrição (opcional)
                  </label>
                  <input
                    type="text"
                    value={uploadDesc}
                    onChange={(e) => setUploadDesc(e.target.value)}
                    placeholder="Ex: Frente do RG"
                    className="w-full px-3 py-2 rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] text-sm"
                  />
                </div>
                <button
                  onClick={handleUpload}
                  disabled={!uploadFile || uploading}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50"
                >
                  {uploading ? 'Enviando...' : 'Enviar'}
                </button>
              </div>
            </div>
          )}

          {/* Document list */}
          {docLoading ? (
            <div className="text-center py-4 text-[color:var(--text-secondary)] text-sm">
              Carregando documentos...
            </div>
          ) : docs.length === 0 ? (
            <div className="text-center py-8 text-[color:var(--text-secondary)] text-sm">
              Nenhum documento encontrado
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {docs.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-[color:var(--border)] bg-[color:var(--bg)]"
                >
                  {/* File type icon */}
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg ${
                      doc.mime_type && doc.mime_type.startsWith('image')
                        ? 'bg-blue-50 text-blue-500'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {doc.mime_type && doc.mime_type.startsWith('image') ? '\uD83D\uDDBC' : '\uD83D\uDCC4'}
                  </div>

                  {/* File info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[color:var(--text)] truncate">
                      {doc.nome_arquivo}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-teal-50 text-teal-700">
                        {DOC_CATEGORIES[doc.categoria] || doc.categoria}
                      </span>
                      {doc.created_at && (
                        <span className="text-[10px] text-[color:var(--text-secondary)]">
                          {new Date(doc.created_at).toLocaleDateString('pt-BR')}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        dpService.downloadDocumento(funcionarioId, doc.id)
                          .then((res) => {
                            const url = URL.createObjectURL(
                              new Blob([res.data], { type: doc.mime_type })
                            );
                            const a = document.createElement('a');
                            a.href = url;
                            a.target = '_blank';
                            a.rel = 'noopener';
                            a.click();
                            setTimeout(() => URL.revokeObjectURL(url), 30000);
                          })
                          .catch((err) => window.alert('Erro ao abrir: ' + err.message));
                      }}
                      className="p-1 text-blue-500 hover:text-blue-700 text-sm"
                      title="Abrir"
                    >
                      {'\u2197'}
                    </button>
                    {podeEditar && (
                      <button
                        onClick={() => excluirDoc(doc.id)}
                        className="p-1 text-red-400 hover:text-red-600 text-sm"
                        title="Excluir"
                      >
                        {'\uD83D\uDDD1'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
